import type { FlightCardData } from '@massilia/queries'

import { ChevronDown, ChevronUp } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { useDebug } from './debug-context'
import { FlightCardSegment } from './flight-card-segment'

interface FlightInfoSectionProps {
  flight: FlightCardData
}

export function FlightInfoSection({ flight }: FlightInfoSectionProps) {
  const showDebug = useDebug()
  // Check if this is a multi-destination flight
  const isMultiDestination = flight.segments && flight.segments.length > 0
  const hasReturn = !isMultiDestination && flight.return

  return (
    <>
      {/* Multi-destination segments */}
      {isMultiDestination && flight.segments ? (
        <div className="flex flex-col gap-2">
          {flight.segments.map((segment, index) => (
            <div
              className={index > 0 ? 'border-t border-gray-100 pt-2' : ''}
              key={`segment-${index}`}
            >
              <FlightCardSegment segment={segment} segmentType={index + 1} />
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {/* Outbound flight segment */}
          <FlightCardSegment segment={flight.outbound} segmentType="outbound" />
          {/* Return flight segment if round trip */}
          {hasReturn && flight.return && (
            <div className="border-t border-gray-100 pt-2">
              <FlightCardSegment segment={flight.return} segmentType="return" />
            </div>
          )}
        </div>
      )}

      {showDebug && <DebugPanel flight={flight} />}
    </>
  )
}

function copy(value: string, message: string) {
  void navigator.clipboard.writeText(value).then(() => {
    toast.info(message)
  })
}

/**
 * Ouvert d'office : le mode debug ne s'active qu'à la demande, et devoir déplier
 * chaque carte pour lire la règle appliquée coûtait plus que la hauteur gagnée.
 * Le repli reste disponible carte par carte quand la liste devient illisible.
 */
function DebugPanel({ flight }: { flight: FlightCardData }) {
  const [isOpen, setIsOpen] = useState(true)
  const { installmentMatchingRuleId } = flight

  return (
    <div className="mt-1 rounded border border-yellow-200 bg-yellow-50/60">
      <button
        aria-expanded={isOpen}
        className="flex w-full cursor-pointer items-center gap-1 px-2 py-1 font-mono text-[11px] font-bold text-yellow-800"
        onClick={() => {
          setIsOpen((v) => !v)
        }}
        type="button"
      >
        {isOpen ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
        debug
        {flight.appliedRules && flight.appliedRules.length > 0 && (
          <span className="ml-1 font-normal text-red-700">{flight.appliedRules.join(' · ')}</span>
        )}
      </button>

      {isOpen && (
        <div className="flex flex-col gap-1 border-t border-yellow-200 px-2 py-1.5 font-mono text-[11px] text-yellow-900">
          <div>
            <span className="font-bold">Transaction ID :</span>{' '}
            <button
              className="cursor-pointer break-all select-all hover:underline"
              onClick={() => {
                copy(flight.transactionId, 'Transaction ID copiée dans le presse-papier')
              }}
              title="Cliquez pour copier dans le presse-papier"
              type="button"
            >
              {flight.transactionId}
            </button>
          </div>

          {installmentMatchingRuleId && (
            <div className="text-green-800">
              <span className="font-bold">Règle paiement en plusieurs fois :</span>{' '}
              <button
                className="cursor-pointer hover:underline"
                onClick={() => {
                  copy(installmentMatchingRuleId, 'ID copié')
                }}
                type="button"
              >
                {installmentMatchingRuleId}
              </button>
            </div>
          )}

          {flight.debugId && (
            <div className="text-gray-700">
              <span className="font-bold">ID :</span> {flight.debugId}
            </div>
          )}

          {flight.seatCode && (
            <div className="text-purple-800">
              <span className="font-bold">Seat code :</span> {flight.seatCode}
            </div>
          )}

          {flight.originalCheckedBags !== undefined && (
            <div className="text-orange-800">
              <span className="font-bold">R9 :</span> Bagage initialement à{' '}
              {flight.originalCheckedBags}PC, forcé à {flight.minCheckedBags}PC
            </div>
          )}

          {flight.originalCheckedBagsWeightKg !== undefined && (
            <div className="text-orange-800">
              <span className="font-bold">
                {flight.appliedRules?.includes('R48') ? 'R48' : 'R14'} :
              </span>{' '}
              Poids bagage initialement à {flight.originalCheckedBagsWeightKg}kg, forcé à{' '}
              {flight.minCheckedBagsWeightKg}kg
            </div>
          )}

          {flight.appliedRules && flight.appliedRules.length > 0 && (
            <div className="flex flex-wrap items-center gap-1">
              {flight.appliedRules.map((rule) => (
                <span
                  className="rounded border border-red-200 bg-red-50 px-1.5 py-0.5 font-bold text-red-700"
                  key={rule}
                >
                  {rule}
                </span>
              ))}
              {flight.collidedWithDebugId && (
                <button
                  className="cursor-pointer rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 font-bold text-blue-700 hover:bg-blue-100"
                  onClick={() => {
                    const el = document.getElementById(`offer-${flight.collidedWithDebugId}`)
                    el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                    el?.classList.add('ring-2', 'ring-blue-500')
                    setTimeout(() => el?.classList.remove('ring-2', 'ring-blue-500'), 2000)
                  }}
                  type="button"
                >
                  → Voir {flight.collidedWithDebugId}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
